import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CalendarButton from './CalendarButton';
import { LINK_TYPES, LINK_TYPE_KEYS } from '../../lib/todoLinks';
import { calendarState, PRIORITIES } from '../../lib/todos';

const WHEN = new Intl.DateTimeFormat('en-US', {
  day: 'numeric',
  month: 'short',
  hour: 'numeric',
  minute: '2-digit',
});

const ACTIVITY_LABEL = {
  created: 'Created',
  completed: 'Completed',
  reopened: 'Reopened',
  rescheduled: 'Rescheduled',
  snoozed: 'Snoozed',
};

/* A right-side drawer, not a modal.
 *
 * The list stays visible beside it, so editing one task doesn't take the
 * page away from you and you keep your place in the day.
 */
export default function TaskDrawer({ task, profiles, onClose, onSave, onDelete, onCalendarAdded }) {
  const navigate = useNavigate();
  const [draft, setDraft] = useState({
    title: task.title || '',
    notes: task.notes || '',
    due_date: task.due_date || '',
    due_time: task.due_time ? String(task.due_time).slice(0, 5) : '',
    priority: task.priority || 'Normal',
    assigned_to: task.assigned_to || '',
    link_type: task.link_type || '',
    link_id: task.link_id || '',
  });
  const [options, setOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // The list for a link type is fetched only when that type is chosen —
  // there's no reason to load every invoice to show a task.
  useEffect(() => {
    if (!draft.link_type) {
      setOptions([]);
      return;
    }
    let cancelled = false;
    setLoadingOptions(true);
    LINK_TYPES[draft.link_type]
      .list()
      .then((rows) => !cancelled && setOptions(rows))
      .catch(() => !cancelled && setOptions([]))
      .finally(() => !cancelled && setLoadingOptions(false));
    return () => {
      cancelled = true;
    };
  }, [draft.link_type]);

  const set = (key) => (e) => setDraft((d) => ({ ...d, [key]: e.target.value }));

  const handleSave = async () => {
    if (!draft.title.trim()) {
      setError('A task needs a title.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(task, {
        title: draft.title.trim(),
        notes: draft.notes.trim() || null,
        due_date: draft.due_date || null,
        due_time: draft.due_time || null,
        priority: draft.priority,
        assigned_to: draft.assigned_to || null,
        link_type: draft.link_id ? draft.link_type : null,
        link_id: draft.link_id || null,
      });
      onClose();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  const link = task.link_type ? LINK_TYPES[task.link_type] : null;
  const activity = Array.isArray(task.activity) ? task.activity : [];

  return (
    <>
      <div className="drawer-scrim" onClick={onClose} />
      <aside className="task-drawer" role="dialog" aria-modal="true" aria-label={task.title}>
        <header className="task-drawer-head">
          <button className="asset-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>

        <label className="drawer-field">
          Title
          <input value={draft.title} onChange={set('title')} />
        </label>

        <label className="drawer-field">
          Description
          <textarea rows={4} value={draft.notes} onChange={set('notes')} />
        </label>

        <div className="drawer-grid">
          <label className="drawer-field">
            Due date
            <input type="date" value={draft.due_date} onChange={set('due_date')} />
          </label>
          <label className="drawer-field">
            Time
            <input
              type="time"
              value={draft.due_time}
              onChange={set('due_time')}
              disabled={!draft.due_date}
            />
          </label>
          <label className="drawer-field">
            Priority
            <select value={draft.priority} onChange={set('priority')}>
              {/* A task still carrying the retired 'Low' keeps it as an
                  option, so opening the drawer can't quietly re-rank it. */}
              {[...new Set([...PRIORITIES, draft.priority])].filter(Boolean).map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </label>
          <label className="drawer-field">
            Assignee
            <select value={draft.assigned_to} onChange={set('assigned_to')}>
              <option value="">Unassigned</option>
              {profiles.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.display_name || p.email}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="drawer-field">
          <span>Link to</span>
          <div className="drawer-link-row">
            <select
              value={draft.link_type}
              onChange={(e) => setDraft((d) => ({ ...d, link_type: e.target.value, link_id: '' }))}
            >
              <option value="">Nothing</option>
              {LINK_TYPE_KEYS.map((key) => (
                <option key={key} value={key}>
                  {LINK_TYPES[key].label}
                </option>
              ))}
            </select>
            {draft.link_type && (
              <select value={draft.link_id} onChange={set('link_id')} disabled={loadingOptions}>
                <option value="">{loadingOptions ? 'Loading…' : 'Choose…'}</option>
                {options.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* The contextual action: a task about a formula can start a batch
            from here, which is what makes linking worth doing at all. */}
        {link && task.link_id && (
          <button
            className="primary-action drawer-action"
            onClick={() => navigate(link.action.to(task.link_id))}
          >
            {link.action.label}
          </button>
        )}

        <CalendarButton task={task} variant="full" onAdded={() => onCalendarAdded(task)} />

        {calendarState(task) === 'stale' && (
          <p className="drawer-note">
            You'll need to delete the old event in Google Calendar — adding again creates a second
            one rather than moving the first.
          </p>
        )}

        {error && <p className="formula-error">{error}</p>}

        <div className="drawer-actions">
          <button className="ghost-button" onClick={() => onDelete(task)}>
            Delete
          </button>
          <button className="primary-action" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>

        {activity.length > 0 && (
          <section className="drawer-activity">
            <h4>Activity</h4>
            <ul>
              {[...activity].reverse().map((entry, i) => (
                <li key={i}>
                  <strong>{ACTIVITY_LABEL[entry.kind] || entry.kind}</strong>
                  {entry.detail && <span>{entry.detail}</span>}
                  <em>{WHEN.format(new Date(entry.at))}</em>
                </li>
              ))}
            </ul>
          </section>
        )}
      </aside>
    </>
  );
}
